import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Fade,
  IconButton,
  LinearProgress,
  Avatar,
  Chip,
} from "@mui/material";
import { X, BadgeCheck } from "lucide-react";
import { brand } from "brand/theme";

const ACCENT = brand.colors.accent;
const CRITICAL = brand.colors.critical;
const INK = brand.colors.ink;
const MUTED = brand.colors.muted;
const LINE = brand.colors.line;
const SURFACE_2 = "#F0F0F2";
const FONT_BODY = brand.fonts.body;
const FONT_DISPLAY = brand.fonts.display;

/** Below header; leaves room for action snackbars stacked above. */
const TOAST_TOP = 168;

const NotificationToast = ({ notification, onClose }) => {
  const [visible, setVisible] = useState(true);
  const [progress, setProgress] = useState(100);
  const DISPLAY_DURATION = 5000;
  const ANIMATION_DURATION = 300;

  const baseUrl = import.meta.env.VITE_APP_API_URL.slice(0, -1);

  useEffect(() => {
    let timer;
    let progressInterval;

    if (visible) {
      progressInterval = setInterval(() => {
        setProgress((prevProgress) => {
          const newProgress = prevProgress - (50 / DISPLAY_DURATION) * 100;
          return newProgress > 0 ? newProgress : 0;
        });
      }, 50);

      timer = setTimeout(() => {
        setVisible(false);
        setTimeout(() => {
          if (onClose) onClose();
        }, ANIMATION_DURATION);
      }, DISPLAY_DURATION);
    }

    return () => {
      clearTimeout(timer);
      clearInterval(progressInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose]);

  const handleClick = () => {
    if (!notification) return;

    let targetUrl = "/";

    if (
      notification.notification_type === "REPORT" ||
      (notification.data && notification.data.report_id)
    ) {
      targetUrl = `/admin/reports/${notification.data.report_id}`;
    } else if (
      notification.notification_type === "COMMENT" ||
      (notification.data && notification.data.content_type === "comment")
    ) {
      targetUrl = `/admin/contributions/${notification.data.comment_id}`;
    } else if (
      notification.data &&
      notification.data.content_type === "avis" &&
      notification.data.content_id
    ) {
      targetUrl = `/avis/${notification.data.content_id}?admin_review=true`;
    }

    window.location.href = targetUrl;
    setVisible(false);
    if (onClose) onClose();
  };

  if (!notification) return null;

  const isReport =
    notification.notification_type === "REPORT" ||
    (notification.data && notification.data.report_id);
  const isComment =
    notification.notification_type === "COMMENT" ||
    (notification.data && notification.data.content_type === "comment");

  const barColor = isReport ? CRITICAL : ACCENT;

  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return "À l'instant";
    if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `Il y a ${minutes} minute${minutes > 1 ? "s" : ""}`;
    }
    if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `Il y a ${hours} heure${hours > 1 ? "s" : ""}`;
    }
    const days = Math.floor(diffInSeconds / 86400);
    return `Il y a ${days} jour${days > 1 ? "s" : ""}`;
  };

  const truncateText = (text, maxLength) => {
    if (!text) return "";
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  };

  const getFullProfileImageUrl = (profileImagePath) => {
    if (!profileImagePath) return null;
    return profileImagePath.startsWith("http")
      ? profileImagePath
      : `${baseUrl}${profileImagePath}`;
  };

  const getContentTypeLabel = (type) => {
    switch (type) {
      case "medication":
        return "Médicament";
      case "expert":
        return "Expert";
      case "expert_opinion":
        return "Avis médical";
      case "institution":
        return "Institution";
      case "news":
        return "Actualité";
      case "comment":
        return "Contribution";
      default:
        return type
          ? type.charAt(0).toUpperCase() + type.slice(1)
          : "Information";
    }
  };

  const user = notification.data?.user || {};
  const reporter = notification.data?.reporter || {};
  const contentObject = notification.data?.content_object || {};

  let cleanTitle = notification.title || "";
  cleanTitle = cleanTitle.replace(/^Nouvelle contribution:\s*/i, "");
  cleanTitle = cleanTitle.replace(/^Nouveau signalement:\s*/i, "");

  const displayName = isReport
    ? reporter?.username
    : user?.full_name || "Utilisateur";
  const avatarSrc = getFullProfileImageUrl(
    isReport ? reporter?.profile_image : user?.profile_image_url,
  );
  const initials = (displayName || "U")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");

  const chipLabel = isReport
    ? notification.data?.report_type || "Signalement"
    : getContentTypeLabel(notification.data?.content_type);

  return (
    <Fade in={visible}>
      <Paper
        elevation={0}
        sx={{
          position: "fixed",
          top: TOAST_TOP,
          right: 16,
          width: 340,
          zIndex: 2000,
          overflow: "hidden",
          borderRadius: 0,
          cursor: "pointer",
          backgroundColor: brand.colors.surface,
          border: `1px solid ${LINE}`,
          boxShadow: "0 16px 40px rgba(17, 24, 39, 0.12)",
          fontFamily: FONT_BODY,
          "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 4,
            backgroundColor: barColor,
            zIndex: 1,
          },
          "&:hover": {
            borderColor: brand.colors.accentBorder,
          },
        }}
        onClick={handleClick}
      >
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 2,
            bgcolor: brand.colors.accentSoft,
            "& .MuiLinearProgress-bar": {
              backgroundColor: barColor,
              borderRadius: 0,
            },
          }}
        />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            pl: 2.25,
            pr: 1.5,
            py: 1.75,
            gap: 1.25,
            bgcolor: notification.is_read
              ? "transparent"
              : brand.colors.accentSoft,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              width: "100%",
              gap: 1,
            }}
          >
            <Avatar
              src={avatarSrc || undefined}
              alt={displayName}
              sx={{
                width: 36,
                height: 36,
                borderRadius: 0,
                bgcolor: SURFACE_2,
                color: MUTED,
                fontSize: "0.8rem",
                fontFamily: FONT_BODY,
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {initials}
            </Avatar>

            <Box sx={{ minWidth: 0, flex: 1, pt: 0.15 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  minWidth: 0,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: FONT_DISPLAY,
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    color: INK,
                    lineHeight: 1.25,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {displayName}
                </Typography>
                {!isReport && user?.is_verified && (
                  <BadgeCheck
                    color="white"
                    fill={ACCENT}
                    size={15}
                    strokeWidth={1.3}
                  />
                )}
              </Box>
              <Typography
                sx={{
                  mt: 0.2,
                  fontFamily: FONT_BODY,
                  fontSize: "0.75rem",
                  color: MUTED,
                  lineHeight: 1.3,
                }}
              >
                {formatRelativeTime(notification.created_at)}
              </Typography>
            </Box>

            <IconButton
              size="small"
              aria-label="Fermer"
              onClick={(e) => {
                e.stopPropagation();
                setVisible(false);
                if (onClose) setTimeout(onClose, ANIMATION_DURATION);
              }}
              sx={{
                color: MUTED,
                borderRadius: 0,
                p: 0.5,
                mt: -0.25,
                flexShrink: 0,
                "&:hover": {
                  bgcolor: brand.colors.accentSoft,
                  color: ACCENT,
                },
              }}
            >
              <X size={14} />
            </IconButton>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              gap: 1,
            }}
          >
            <Typography
              sx={{
                fontFamily: FONT_BODY,
                fontWeight: 600,
                fontSize: "0.8rem",
                textTransform: "uppercase",
                letterSpacing: "0.02em",
                color: INK,
                maxWidth: "62%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {truncateText(contentObject?.title || cleanTitle, 40)}
            </Typography>

            <Chip
              label={chipLabel}
              size="small"
              sx={{
                height: 22,
                borderRadius: 0,
                bgcolor: isReport
                  ? brand.colors.criticalSoft
                  : brand.colors.accentSoft,
                color: isReport ? CRITICAL : ACCENT,
                border: `1px solid ${
                  isReport
                    ? "rgba(194, 65, 12, 0.35)"
                    : brand.colors.accentBorder
                }`,
                fontWeight: 600,
                fontSize: "0.7rem",
                fontFamily: FONT_BODY,
                maxWidth: "38%",
                "& .MuiChip-label": { px: 1 },
              }}
            />
          </Box>

          {isComment && notification.data?.content && (
            <Typography
              sx={{
                fontFamily: FONT_BODY,
                fontSize: "0.85rem",
                color: MUTED,
                lineHeight: 1.45,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {truncateText(notification.data.content, 100)}
            </Typography>
          )}
        </Box>
      </Paper>
    </Fade>
  );
};

export default NotificationToast;
